<template>
  <rest-page
    :key="key"
    class="push-settings-tab"
    :fetch-endpoint="fetchEndpoint"
  >
    <rest-form
      ref="form"
      :save-endpoint="saveEndpoint"
      :after-save="afterSave"
      :model="model"
      :rules="rules"
      :disabled="false"
    >
      <el-row>
        <el-col :xs="9" :sm="9" :md="8" :lg="6">
          <global-settings-nav
            :items="menuItems"
            :router="true"
            :default-active="activeItem"
          />
        </el-col>
        <el-col :xs="15" :sm="15" :md="16" :lg="18">
          <rest-page
            v-if="!isLoading"
            :key="$route.query.id"
            :fetch-endpoint="fetchAppEndpoint"
          >
            <push-settings-web
              v-if="model.type === ApplicationTypes.web"
              :model="model"
              :date-format="dateFormat"
              :readonly="readonly"
              :save="save"
            />
            <push-settings-ios
              v-if="model.type === ApplicationTypes.ios"
              :model="model"
              :date-format="dateFormat"
              :readonly="readonly"
              :save="save"
            />
            <push-settings-android
              v-if="model.type === ApplicationTypes.android"
              :model="model"
              :readonly="readonly"
              :save="save"
            />
            <div class="no-apps" v-if="!model.type">
              <div v-if="$route.query.type === ApplicationTypes.ios">
                <h2>{{ $t('PushSettingsTab_NoIosApps') }}</h2>
                <div
                  class="desc"
                  v-html="$t('PushSettingsTab_NoIosApps_Desc')"
                />
              </div>
              <div v-else-if="$route.query.type === ApplicationTypes.android">
                <h2>{{ $t('PushSettingsTab_NoAndroidApps') }}</h2>
                <div
                  class="desc"
                  v-html="$t('PushSettingsTab_NoAndroidApps_Desc')"
                />
              </div>
              <div v-else>
                <h2>{{ $t('PushSettingsTab_NoWebApps') }}</h2>
                <div
                  class="desc"
                  v-html="$t('PushSettingsTab_NoWebApps_Desc')"
                />
              </div>
            </div>
          </rest-page>
        </el-col>
      </el-row>
    </rest-form>
  </rest-page>
</template>

<script>
import { projectEndpoint, projectRoute } from '@/core/helpers'
import RestPage from '@/common/components/rest/RestPage'
import GlobalSettingsNav from '@/common/components/navigation/GlobalSettingsNav'
import PushSettingsWeb from '@/modules/settings/components/PushSettingsWeb'
import PushSettingsIos from '@/modules/settings/components/PushSettingsIos'
import PushSettingsAndroid from '@/modules/settings/components/PushSettingsAndroid'
import RestForm from '@/common/components/rest/RestForm'
import { ApplicationTypes } from '@/modules/account/enum/ApplicationEnum'
import i18n from '@/core/utils/I18n'
import moment from 'moment'
import { RestApiPaginator } from '@/core/utils/RestApiPaginator'
export default {
  name: 'PushSettingsTab',
  components: {
    RestForm,
    PushSettingsAndroid,
    PushSettingsIos,
    PushSettingsWeb,
    GlobalSettingsNav,
    RestPage
  },
  props: {
    projectId: {
      required: false
    }
  },
  data() {
    return {
      activeItem: null,
      ApplicationTypes,
      model: {},
      applications: [],
      key: '',
      isLoading: true
    }
  },
  methods: {
    save() {
      this.$refs['form'].save()
    },
    afterSave(data, finishLoading) {
      this.key = new Date().getTime() // refresh the table items
      finishLoading.apply()
    },
    createRoute(id, appType) {
      let route = {
        name: 'PushSettingsTab',
        query: {}
      }
      if (appType) {
        route.query.type = appType
      } else {
        route.query.id = id
      }
      return projectRoute(route, this.$route.query.project_id)
    },

    webValidation(item) {
      let icon = 'el-icon-empty-icon'

      if (item.is_safari) {
        if (
          item.safari_push_id &&
          item.safari_key &&
          item.web_icon_small &&
          item.web_icon_large
        ) {
          icon = 'el-icon-success'
        } else {
          return 'el-icon-empty-icon'
        }
      }

      if (item.is_chrome || item.is_firefox) {
        if (item.webpush_domain_hosted || item.webpush_domain) {
          icon = 'el-icon-success'
        } else {
          return 'el-icon-empty-icon'
        }
      }

      return icon
    },
    getGlobalStatus(apps) {
      let icon = apps.find(item => item.icon === 'el-icon-success')
        ? 'el-icon-success'
        : 'el-icon-empty-icon'
      icon = apps.find(item => item.icon === 'el-icon-error')
        ? 'el-icon-error'
        : icon
      return apps.find(item => item.icon === 'el-icon-empty-icon')
        ? 'el-icon-empty-icon'
        : icon
    }
  },
  computed: {
    readonly() {
      return !this.$can('updateProject')
    },
    menuItems() {
      return [
        {
          title: this.$t('PushSettingsTab_NavItem_Ios'),
          route: this.createRoute('', ApplicationTypes.ios),
          subMenuItems: this.iosApps.length ? this.iosApps : null,
          icon: this.getGlobalStatus(this.iosApps)
        },
        {
          title: this.$t('PushSettingsTab_NavItem_Android'),
          route: this.createRoute('', ApplicationTypes.android),
          subMenuItems: this.androidApps.length ? this.androidApps : null,
          icon: this.getGlobalStatus(this.androidApps)
        },
        {
          title: this.$t('PushSettingsTab_NavItem_Web'),
          route: this.createRoute('', ApplicationTypes.web),
          subMenuItems: this.webApps.length ? this.webApps : null,
          icon: this.getGlobalStatus(this.webApps)
        }
      ]
    },
    iosApps() {
      return this.applications
        .filter(app => app.type === ApplicationTypes.ios)
        .map(app => ({
          title: app.name,
          route: this.createRoute(app.id),
          index: 'app-' + app.id,
          icon:
            app.ios_production_key && app.ios_sandbox_key
              ? 'el-icon-success'
              : 'el-icon-empty-icon'
        }))
    },
    androidApps() {
      return this.applications
        .filter(app => app.type === ApplicationTypes.android)
        .map(app => ({
          title: app.name,
          route: this.createRoute(app.id),
          index: 'app-' + app.id,
          icon: app.android_key ? 'el-icon-success' : 'el-icon-empty-icon'
        }))
    },
    webApps() {
      return this.applications
        .filter(app => app.type === ApplicationTypes.web)
        .map(app => ({
          title: app.name,
          route: this.createRoute(app.id),
          index: 'app-' + app.id,
          icon: this.webValidation(app)
        }))
    },
    saveEndpoint() {
      if (this.$route.query.id) {
        return projectEndpoint(
          {
            method: 'patch',
            url: `applications/${this.$route.query.id}`
          },
          this.project_id || this.$route.query.project_id
        )
      } else {
        return {}
      }
    },
    fetchAppEndpoint() {
      if (this.$route.query.id) {
        return projectEndpoint(
          {
            method: 'get',
            url: `applications/${this.$route.query.id}`,
            params: {
              expand:
                'webpushCode,webpushServiceWorkerContent,webpushManifestContent,webpushWindowContent,' +
                'webpushFrameContent,safariCertificateExpiryDate,iosProductionCertificateExpiryDate,' +
                'iosSandboxCertificateExpiryDate,providerDomain'
            },
            handler: data => {
              this.model = { ...data }
            }
          },
          this.projectId || this.$route.query.project_id
        )
      } else {
        this.model = {}
        return this.fetchEndpoint // reload menu items
      }
    },
    fetchEndpoint() {
      this.isLoading = true
      new RestApiPaginator(
        projectEndpoint(
          {
            method: 'get',
            url: 'applications',
            params: {}
          },
          this.project_id || this.$route.query.project_id
        )
      )
        .fetchAllPages()
        .then(data => {
          if (data && data.length) {
            if (
              !this.$route.query.id &&
              !this.$route.query.type &&
              data[0].id
            ) {
              // just open first app
              this.$router.push({
                path: `${this.$route.fullPath}&id=${data[0].id}`
              })
            }
            this.applications = data
            this.activeItem = 'app-' + this.$route.query.id
            this.isLoading = false
          }
        })
    },
    rules() {
      return {}
    },
    dateFormat() {
      const locale = i18n.locale === 'en' ? 'en-GB' : i18n.locale
      return moment.localeData(locale).longDateFormat('llll')
    }
  },
  watch: {
    model: {
      handler(val) {
        this.applications.forEach((app, key) => {
          if (app.id === this.$route.query.id) {
            Object.assign(this.applications[key], val)
          }
        })
      },
      deep: true
    }
  }
}
</script>
<style scoped lang="scss">
.push-settings-tab {
  background-color: #ffffff;
  .page {
    background-color: #ffffff;
  }
  .no-apps {
    padding-left: 50px;
    .desc {
      font-size: 14px;
      padding: 16px 0;
    }
  }
}
</style>
<style scoped>
>>> .el-loading-spinner {
  top: 15%;
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/components/PushSettingsTab.vue